import { Button } from "@mindlercare/ui-web";
import { Typography } from "components/typography";
import { ReactQueryV5 } from "@mindlercare/mindlerapi-client";
const { useMutation } = ReactQueryV5;
import { useMindlerApi } from "api/apiv2";
import CustomModal from "components/lib/customModal";
import { useInvalidateSlots } from "components/booking/hooks/useInvalidateSlots";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { styled } from "styled-components";
import { TemplateName } from "../../../core/types";
import { ErrorText } from "./errorText";
import { useCloseDialog } from "./actions";

type Props = {
  slot: {
    slotId: number;
    patientId: number | null;
    psychologistUserId: number;
    templateName: TemplateName | undefined;
  };
  open: boolean;
  setModalOpen: (open: boolean) => void;
};

export const DeleteOrCancelSlotModal = ({
  slot,
  open,
  setModalOpen,
}: Props) => {
  const { t } = useTranslation();
  const invalidateSlots = useInvalidateSlots();
  const closeModal = () => {
    setModalOpen(false);
  };
  const closeDialogWithoutHistory = useCloseDialog(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const api = useMindlerApi();
  const { mutateAsync: deleteSlot, isPending: isCancelPending } = useMutation(
    api.slots.psychologist.delete.mutationOptions(),
  );
  const { mutateAsync: cancelSlot, isPending: isDeletePending } = useMutation(
    api.slots.psychologist.cancel.mutationOptions(),
  );
  const handleDelete = async () => {
    await deleteSlot(
      {
        slotId: slot.slotId,
        psychologistUserId: slot.psychologistUserId,
      },
      {
        onError: (err) => {
          setErrorMessage(err.message);
        },
      },
    );
    closeModal();
    await closeDialogWithoutHistory();
    await invalidateSlots();
  };
  const handleCancel = async () => {
    await cancelSlot(
      {
        slotId: slot.slotId,
      },
      {
        onError: (err) => {
          setErrorMessage(err.message);
        },
      },
    );
    await closeDialogWithoutHistory();
    await invalidateSlots();
  };

  const isSlotUnbooked = slot.patientId === null;
  const isDeletableTemplate = ["tech_test"].includes(slot.templateName ?? "");
  const mode = isSlotUnbooked || isDeletableTemplate ? "DELETE" : "CANCEL";

  return (
    <CustomModal.Wrapper
      onClose={closeModal}
      open={open}
      size="large"
      isScrollable={false}
    >
      <Container>
        <Typography variant="h3">
          {mode === "DELETE"
            ? t("REMOVE_SLOT_CONFIRM")
            : t("CANCEL_SLOT_CONFIRM")}
        </Typography>
        <ButtonRow>
          <Button variant="secondary" onClick={closeModal}>
            {t("CANCEL")}
          </Button>
          <Button
            testId="pw-cancel-or-delete-slot-confirm"
            variant="primary"
            disabled={isCancelPending || isDeletePending}
            onClick={mode === "DELETE" ? handleDelete : handleCancel}
          >
            {mode === "DELETE" ? t("REMOVE_TIMESLOT") : t("CANCEL_TIMESLOT")}
          </Button>
        </ButtonRow>
        {errorMessage && <ErrorText>{errorMessage}</ErrorText>}
      </Container>
    </CustomModal.Wrapper>
  );
};

export const Container = styled.div`
  display: grid;
  gap: 24px;
  justify-items: center;
  text-align: center;
`;

const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
`;
